// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListProductImage from "@tps/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@tps/Overrides/Widgets/ProductList/ProductListProductInformation";
import { mergeWithCss } from "@tps/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & {
    trackProductRecommendation?: (isNewPageWasOpen: boolean) => void;
    extendedStyles?: ProductListProductGridCardStyles;
    // Xng-Changes
    redirectToParent?: boolean;
    isPlp?: boolean;
} & HasProduct;

export interface ProductListProductGridCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    sponsoredText?: TypographyPresentationProps;
}

export const productGridCardStyles: ProductListProductGridCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            height: 100%;
            overflow: hidden;
            padding-bottom: 10px;
        `,
    },
    imageWrapper: {
        css: css`
            margin: auto;
            padding: 10px 50px;
        `,
    },
    sponsoredText: {
        css: css`
            font-size: 0.9em;
            opacity: 0.5;
            position: absolute;
            top: 12px;
        `,
    },
};

const ProductListProductGridCard: FC<Props> = ({
    product,
    showImage,
    showCompare,
    showAttributes,
    showAvailability,
    showBrand,
    showPartNumbers,
    showTitle,
    showAddToList,
    showPrice,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    trackProductRecommendation,
    extendedStyles,
    // Xng-Changes
    redirectToParent,
    isPlp,
}) => {
    const [styles] = useState(() => mergeWithCss(productGridCardStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`productListProductCard${product.id}`}>
            <StyledWrapper {...styles.imageWrapper}>
                {product.isSponsored && <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>}
                <ProductListProductImage
                    showImage={showImage}
                    showCompare={showCompare}
                    showImageBadge={showImageBadges}
                    showTextBadge={showTextBadges}
                    // XNG-Changes
                    showImageBadges={showImageBadges}
                    maxImageBadges={maximumNumberOfImageBadges}
                    {...(trackProductRecommendation && { onClick: trackProductRecommendation })}
                    // Xng-Changes: TPS-647
                    redirectToParent={redirectToParent}
                />
            </StyledWrapper>
            <ProductListProductInformation
                showAttributes={showAttributes}
                showAvailability={showAvailability}
                showBrand={showBrand}
                showPartNumbers={showPartNumbers}
                showTitle={showTitle}
                // showImageBadges={showImageBadges}
                // maxImageBadges={maximumNumberOfImageBadges}
                showTextBadges={showTextBadges}
                maxTextBadges={maximumNumberOfTextBadges}
                {...(trackProductRecommendation && { onClick: trackProductRecommendation })}
                // Xng-Changes: TPS-647
                redirectToParent={redirectToParent}
                isPlp={isPlp}
            />
            {/* <ProductListActions showAddToList={showAddToList} showPrice={showPrice} /> */}
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductGridCard);
